import { useSyncExternalStore } from "react";
import type { Hasher } from "@hash/shared";
import { api } from "../api/client";

/**
 * Client-side store of the whole hasher roster.
 *
 * The roster is short and rarely changes, so instead of hitting the server's
 * scan-and-filter search on every keystroke (five screens used to), we load all
 * hashers once on app open and search them locally. The store refetches only
 * when it's never been loaded, when it's gone stale (older than STALE_MS), or
 * when a caller forces a reload (e.g. after a 409 duplicate-create).
 *
 * It's a module-level singleton (mirroring the module-level `auth` helpers, not
 * a React Context) exposed to components via the `useHashers` hook.
 */

const STALE_MS = 10 * 60 * 1000; // 10 minutes

export type HasherStoreStatus = "idle" | "loading" | "loaded" | "error";

interface HasherStoreState {
  hashers: Hasher[];
  status: HasherStoreStatus;
  loadedAt: number | null;
  error: string | null;
}

let state: HasherStoreState = {
  hashers: [],
  status: "idle",
  loadedAt: null,
  error: null,
};

// Shared in-flight load promise so concurrent callers (app open, a page mount,
// a search input focusing all at once) issue a single network fetch.
let inFlight: Promise<void> | null = null;

const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

function setState(patch: Partial<HasherStoreState>): void {
  state = { ...state, ...patch };
  emit();
}

function isFresh(): boolean {
  return state.loadedAt !== null && Date.now() - state.loadedAt < STALE_MS;
}

// Paginate the cheap alpha-list endpoint (no `q`) to completion.
async function fetchAll(): Promise<Hasher[]> {
  const collected: Hasher[] = [];
  let cursor: string | undefined = undefined;
  do {
    const res = await api.hashers.list({ cursor, limit: 200 });
    collected.push(...res.hashers);
    cursor = res.cursor;
  } while (cursor);
  return collected;
}

async function runLoad(): Promise<void> {
  setState({ status: "loading", error: null });
  try {
    const hashers = await fetchAll();
    setState({ hashers, status: "loaded", loadedAt: Date.now(), error: null });
  } catch (e) {
    setState({
      status: "error",
      error: e instanceof Error ? e.message : "Failed to load hashers",
    });
    throw e;
  } finally {
    inFlight = null;
  }
}

/**
 * Load the roster if it hasn't been loaded or has gone stale. Resolves
 * immediately when already loaded and fresh. Concurrent callers share one fetch.
 */
export function loadHashers(): Promise<void> {
  if (inFlight) return inFlight;
  if (state.status === "loaded" && isFresh()) return Promise.resolve();
  inFlight = runLoad();
  return inFlight;
}

/** Force a refetch regardless of freshness (e.g. after a 409 duplicate). */
export function reloadHashers(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = runLoad();
  return inFlight;
}

/**
 * Case-insensitive substring search over the current snapshot, matching the
 * server's old `name.toLowerCase().includes(q)` semantics. Empty query → [].
 */
export function searchHashers(q: string): Hasher[] {
  const needle = q.trim().toLowerCase();
  if (!needle) return [];
  return state.hashers.filter((h) => h.name.toLowerCase().includes(needle));
}

/**
 * Upsert a hasher (by id) into the store — call after a successful create or
 * edit (an edited/updated Hasher replaces the existing row).
 */
export function addHasher(hasher: Hasher): void {
  const without = state.hashers.filter((h) => h.id !== hasher.id);
  setState({ hashers: [...without, hasher] });
}

/** Remove a hasher (by id) from the store — call after a successful delete. */
export function removeHasher(id: string): void {
  setState({ hashers: state.hashers.filter((h) => h.id !== id) });
}

/** Test-only: reset all state and any in-flight load. */
export function resetHasherStore(): void {
  state = { hashers: [], status: "idle", loadedAt: null, error: null };
  inFlight = null;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): HasherStoreState {
  return state;
}

/** Reactive view of the store for components. */
export function useHashers(): HasherStoreState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
